import { forwardRef, InputHTMLAttributes, useEffect, useState } from 'react';

import { cn } from '@foundation/utils/cn';
import { ErrorMessage } from '@shared/ui';

import { LoadingSpinner } from '../LoadingSpinner';

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  value: string;
  label?: string;
  error?: string;
  isLoading?: boolean;
  endAdornment?: React.ReactNode;
  showRequired?: boolean;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      error,
      isLoading,
      className = '',
      label,
      required,
      endAdornment = null,
      showRequired = false,
      ...props
    }: TextInputProps,
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(
      !!(props.value ?? props.defaultValue),
    );

    // Sync floating label when value is set programmatically (e.g. RHF's setValue)
    useEffect(() => {
      const next = !!props.value;
      setHasValue((prev) => (prev !== next ? next : prev));
    }, [props.value]);

    const isLabelFloating = isFocused || hasValue;

    return (
      <div className="w-full text-primary">
        <label
          className={cn(
            'relative flex flex-col justify-center px-4 text-sm border border-primary rounded-lg min-h-14',
            'focus-within:ring-[1.5px] focus-within:ring-primary',
            {
              'border-2 border-alert-negative-primary': !!error,
              'bg-disabled': props.disabled,
            },
          )}
          data-errors={!!error}
          data-loading={isLoading}
        >
          {label && (
            <span
              className={cn(
                'absolute left-4 transition-all duration-200 ease-out pointer-events-none',
                {
                  'top-1 text-xs': isLabelFloating,
                  'top-1/2 -translate-y-1/2 text-base placeholder-primary':
                    !isLabelFloating,
                },
              )}
            >
              {label}
              {required && showRequired && (
                <span className="text-md text-alert-negative-primary">
                  &nbsp;*
                </span>
              )}
            </span>
          )}
          <input
            type="text"
            required={required}
            ref={ref}
            className={cn(
              'text-base focus:outline-none flex-1 bg-transparent',
              {
                'pt-4': isLabelFloating && label,
              },
              className,
            )}
            data-qa-id={props.name}
            {...props}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              props.onBlur?.(e);
            }}
            onChange={(e) => {
              const next = !!e.target.value;
              setHasValue((prev) => (prev !== next ? next : prev));
              props.onChange?.(e);
            }}
          />

          {endAdornment && (
            <div className="absolute flex items-center right-2 top-1/2 -translate-y-1/2 cursor-pointer px-2">
              {endAdornment}
            </div>
          )}

          {isLoading && (
            <div className="absolute inset-y-0 right-2 flex items-center">
              <LoadingSpinner smallSize />
            </div>
          )}
        </label>
        {error && <ErrorMessage message={error} />}
      </div>
    );
  },
);
TextInput.displayName = 'TextInput';
